/* ============================================================
   RANGKA TUTORIAL — rel kurikulum + ruang kandungan
   ============================================================
   Dipakai oleh KEDUA-DUA halaman:
     · /tutorial            → Overview (Start Here + Chapters + All Lessons)
     · /tutorial/:videoId   → satu lesson

   ⛔ MENU APP TIDAK DISENTUH (arahan user 2026-08-16). Halaman ini
      HANYA melukis rel kurikulumnya sendiri; menu app sebenar datang
      daripada App.jsx — `StudioRail` bila suis v3.9 hidup, `NewSidebar`
      bila mati. Dua kesilapan yang PERNAH dibuat di sini, jangan ulang:
        1. Membalut route dengan `StudioShell` — itu melukis menu
           PROTOTAIP v3.9 di sebelah menu sebenar, jadi pelanggan nampak
           dua menu dan yang salah satu daripadanya.
        2. Menjadikan halaman ini `position: fixed; inset: 0` (corak
           halaman Setting) — itu MENYOROK menu app sepenuhnya.
      Halaman ini duduk dalam susun atur biasa App, sama seperti
      /products dan /notes.

   ⭐ SATU RANGKA, BUKAN DUA HALAMAN BERASINGAN. Rel kurikulum TIDAK
      hilang bila pelanggan menekan Overview — sekali dia masuk, senarai
      chapter kekal di kiri sepanjang masa, sama seperti laman kursus
      (Coursera, Udemy). Overview ialah item PERTAMA dalam rel itu.

   `s-root` WAJIB pada bekas luar: semua token --s-* ditakrif pada kelas
   itu. Dahulu StudioShell yang membawanya; tanpa StudioShell, halaman
   ini kehilangan seluruh palet kalau kelas itu ditanggalkan.

   Rangka ini sengaja BODOH: ia tidak membaca Firestore, tidak menyimpan
   progress, dan tidak tahu apa-apa tentang mod edit. Semua itu milik
   halaman yang memanggilnya.
   ============================================================ */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { alamatLesson, petaKodLesson } from '../data/tutorial';
import { CircleCheck, Circle, Play, LayoutGrid } from 'lucide-react';
import { klikBiasa } from '../utils/klikBiasa';
import { PageHeader } from '../design/ui';
import { useT } from '../i18n';

/* Rel 300px: pada 268px tajuk lesson seperti "Tour webapp: Dashboard
   hingga Workspace" pecah tiga baris dan rel jadi bergerigi.

   Media query TIDAK boleh hidup dalam `style` sebaris, jadi susun atur
   dua lajur ini terpaksa datang daripada blok gaya sebenar. Di bawah
   900px rel jatuh ke BAWAH kandungan: pada skrin sempit, apa yang
   sedang ditonton lebih penting daripada senarai penuh. */
const GAYA = `
.s-tut { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 26px; align-items: start; }
.s-tut__rail { position: sticky; top: 0; max-height: calc(100vh - 130px); overflow-y: auto;
               padding: 0 12px 6px 0; border-right: 1px solid var(--s-line); }
.s-tut__row { display: flex; align-items: flex-start; gap: 9px; width: 100%; text-align: left;
              min-height: 32px; padding: 6px 8px; border: 0; border-radius: 10px;
              background: none; color: var(--s-ink-2); font: inherit; font-size: 13px; line-height: 1.35;
              text-decoration: none; box-sizing: border-box;
              cursor: pointer; transition: background .12s ease, color .12s ease; }
.s-tut__row:hover { background: var(--s-fill); color: var(--s-ink); }
.s-tut__row--on { background: var(--s-fill); color: var(--s-ink); font-weight: 500; }
.s-tut__row--mati { cursor: default; color: var(--s-ink-3); }
.s-tut__row--mati:hover { background: none; color: var(--s-ink-3); }
/* Blok atas rel (Overview + Progress) sengaja BERKONTRA dengan senarai
   lesson di bawahnya: dakwat penuh dan lebih tebal, sementara lesson
   kekal --s-ink-2. Tanpa beza ini semuanya satu warna dan mata tiada
   tempat berlabuh bila rel jadi panjang. */
.s-tut__row--top { color: var(--s-ink); font-weight: 500; }
.s-tut__cap { display: block; font-size: 10px; font-weight: 700; letter-spacing: .08em;
              color: var(--s-ink-3); padding: 0 8px; margin-bottom: 4px; text-transform: uppercase; }
@media (max-width: 900px) {
  .s-tut { grid-template-columns: minmax(0, 1fr); }
  .s-tut__rail { position: static; max-height: none; order: 2; padding: 0; border-right: 0;
                 border-top: 1px solid var(--s-line); padding-top: 18px; }
}
`;

/* Ikon keadaan setiap lesson dalam rel.
   `done` didahulukan daripada `current`: baris yang sedang dibuka sudah
   ditandakan oleh latar terangnya, jadi ikon bebas membawa maklumat yang
   satu lagi — sudah ditonton atau belum. Kalau `current` didahulukan,
   lesson semasa kehilangan tanda siapnya sebaik ia siap. */
function IkonKeadaan({ current, done }) {
    if (done) return <CircleCheck size={14} strokeWidth={2} style={{ flex: 'none', marginTop: 2 }} />;
    if (current) return <Play size={14} strokeWidth={0} fill="currentColor" style={{ flex: 'none', marginTop: 2 }} />;
    return <Circle size={14} strokeWidth={1.9} style={{ flex: 'none', marginTop: 2, opacity: .55 }} />;
}

/* Baris rel = PAUTAN SEBENAR, bukan butang. Klik kanan "Open in new tab",
   klik tengah dan Ctrl+klik mesti berfungsi; klikBiasa memintas klik kiri
   kosong sahaja dan menyerahkan selebihnya kepada browser. Peraturan ini
   ditetapkan 2026-08-16 selepas aduan menu tidak boleh dibuka tab baharu. */
function BarisRel({ href, on, mati, navigate, bolehPergi, children, kelasTambahan = '' }) {
    const kelas = `s-tut__row${kelasTambahan}${on ? ' s-tut__row--on' : ''}${mati ? ' s-tut__row--mati' : ''}`;
    if (mati) return <span className={kelas} aria-disabled="true">{children}</span>;
    return (
        <a
            href={href}
            aria-current={on ? 'page' : undefined}
            onClick={(e) => {
                if (!klikBiasa(e)) return;              // Ctrl/tengah/Shift → biar browser uruskan
                e.preventDefault();
                if (bolehPergi && !bolehPergi()) return; // cth mod edit dengan suntingan belum disimpan
                navigate(href);
            }}
            className={kelas}
        >
            {children}
        </a>
    );
}

/* aktif — 'overview' atau id video yang sedang dibuka.
   bolehPergi — pagar pilihan sebelum meninggalkan halaman semasa. Halaman
     Overview memasangnya supaya klik pada rel semasa mod edit tidak
     membuang suntingan admin tanpa amaran. */
export default function TutorialShell({
    chapters = [], watched, aktif = 'overview',
    akarRef, bolehPergi, subtajuk, tindakan, children,
    /* ⚠️ `data` WAJIB set PENUH lesson, bukan senarai yang sudah ditapis.
       Kod dalam alamat dikira daripada set itu, jadi memberi set berbeza di
       tempat berbeza (cth pelanggan hanya nampak lesson bervideo) boleh
       menghasilkan kod berlainan untuk lesson yang sama — link jadi pecah. */
    data,
}) {
    const navigate = useNavigate();
    const t = useT();

    const flat = chapters.flatMap(b => b.videos);
    // Dikira SEKALI untuk seluruh rel — bukan sekali bagi setiap baris.
    const petaKod = useMemo(() => petaKodLesson(data?.videos), [data]);
    const done = flat.filter(v => watched?.has(v.id)).length;
    const pct = flat.length ? Math.round((done / flat.length) * 100) : 0;

    return (
        <div ref={akarRef} className="s-root sesaat-m-tight">
            <style>{GAYA}</style>

            {/* Kepala halaman = KAWASAN SENDIRI merentas penuh, di ATAS rel
                dan kandungan (arahan user 2026-08-16) — bentuk yang sama
                dengan /workspace (`.s-work__head`: padding 26px 28px 0,
                PageHeader di dalamnya). Ia BUKAN milik rel: tajuk halaman
                yang diselitkan ke dalam rel menjadikannya kelihatan seperti
                nama menu, bukan nama halaman. */}
            <div style={{ padding: '26px 28px 0' }}>
                <PageHeader
                    title={t('tutorial.page.title')}
                    subtitle={subtajuk}
                    actions={tindakan}
                />
            </div>

            <div className="s-tut" style={{ padding: '0 28px 48px' }}>
                <aside className="s-tut__rail">
                    <BarisRel href="/tutorial" on={aktif === 'overview'} navigate={navigate} bolehPergi={bolehPergi} kelasTambahan=" s-tut__row--top">
                        <LayoutGrid size={15} strokeWidth={2} style={{ flex: 'none', marginTop: 1 }} />
                        <span>{t('tutorial.rail.overview')}</span>
                    </BarisRel>

                    {flat.length > 0 && (
                        <div style={{
                            padding: '12px 8px 16px', marginBottom: 14,
                            borderBottom: '1px solid var(--s-line)',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 7 }}>
                                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--s-ink)' }}>{t('tutorial.rail.progress')}</span>
                                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--s-ink)', fontVariantNumeric: 'tabular-nums' }}>
                                    {done}/{flat.length}
                                </span>
                            </div>
                            <div style={{ height: 5, borderRadius: 99, background: 'var(--s-line-strong)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--s-ink)', borderRadius: 99, transition: 'width .4s ease' }} />
                            </div>
                        </div>
                    )}

                    {chapters.map((bab, k) => (
                        <div key={bab.id} style={{ marginBottom: 16 }}>
                            {/* {nama} = bab.label DARI FIRESTORE — hantar sebagai
                                pemboleh ubah, jangan sekali-kali diterjemah. */}
                            <span className="s-tut__cap">{t('tutorial.rail.chapterCap', { nombor: k + 1, nama: bab.label })}</span>
                            {bab.videos.map((v) => (
                                <BarisRel
                                    key={v.id}
                                    href={alamatLesson(v, data, petaKod)}
                                    on={v.id === aktif}
                                    mati={!v.youtubeId}
                                    navigate={navigate}
                                    bolehPergi={bolehPergi}
                                >
                                    <IkonKeadaan current={v.id === aktif} done={watched?.has(v.id)} />
                                    <span style={{ flex: 1, minWidth: 0 }}>{v.title || t('tutorial.untitledLesson')}</span>
                                    {v.duration && (
                                        <span className="s-dim" style={{ flex: 'none', fontSize: 11, marginTop: 1, fontVariantNumeric: 'tabular-nums' }}>
                                            {v.duration}
                                        </span>
                                    )}
                                </BarisRel>
                            ))}
                        </div>
                    ))}
                </aside>

                <main style={{ minWidth: 0 }}>{children}</main>
            </div>
        </div>
    );
}
